import {checklist as backend, common as commonBackend} from '@/api'
import {cloneDeep} from "lodash";
import Checklist from "@/models/Checklist";
import {mergeChecksWithChangeTypes} from "@/utils/checklists/checklist-diff";


let checklist = {

  namespaced: true,

  state: {
    checklists: {
      data: []
    },
    checklistList: [],
    checklist: new Checklist(),
    currChecklist: new Checklist(),
    checklistVersions: []
  },

  mutations: {
    setChecklists: (state, payload) => state.checklists = payload,
    setChecklistVersions: (state, payload) => state.checklistVersions = payload,
    setChecklistsList: (state, payload) => state.checklistList = payload,
    setChecklist: (state, payload) => {
        payload.checks.forEach(check => {
          if (check.percentage_weight == null) {
            check.percentage_weight = 0;
          }
        });
      state.checklist = cloneDeep(payload);
      state.currChecklist = cloneDeep(payload);
    },
    setChecks: (state, payload) => state.checklist.checks = payload,
    name: (state, payload) => state.checklist.name = payload,
    frequency: (state, payload) => {
      state.checklist.frequency = payload
      if(state.checklist.frequency < 1) {
        state.checklist.sets_next_inspection = false
        state.checklist.frequency_exclusions = []
      }
    },
    setFrequencyExclusions: (state, payload) => state.checklist.frequency_exclusions = payload,
    toggleSignatureRequirement: (state) => state.checklist.requires_signature = !state.checklist.requires_signature,
    toggleApproverSignatureRequirement: (state) => state.checklist.requires_approver_signature = !state.checklist.requires_approver_signature,
    toggleInspectionReportImages: (state) => state.checklist.inspection_report_images_inline = !state.checklist.inspection_report_images_inline,
    toggleCustomReport: (state) => state.checklist.use_custom_report = !state.checklist.use_custom_report,
    toggleCustomWeighting: (state) => state.checklist.custom_check_weighting = !state.checklist.custom_check_weighting,
    passPercentage: (state, payload) => state.checklist.pass_percentage = payload,
    imageRule: (state, payload) => state.checklist.image_rule = payload,
    imageCount: (state, payload) => state.checklist.required_image_count = payload,
    toggleUpdatesNextInspection: (state) => state.checklist.sets_next_inspection = !state.checklist.sets_next_inspection,
    addCheck: (state, check) => state.checklist.checks.push(check),
    removeCheck: (state, index) => state.checklist.checks.splice(index, 1),
    clear: (state) => {
      state.checklists = {
        data: []
      }
      state.checklistList = []
      state.checklist = new Checklist
    },
    updateAssetTypes: (state, payload) => state.checklist.asset_types = payload,
    updateChecks(state, updatedCheck) {
      const index = state.checklist.checks.findIndex(c => c.id === updatedCheck.id)
      if (index !== -1) {
        // Ensure replacement for Vue reactivity
        state.checklist.checks.splice(index, 1, { ...updatedCheck })
      }
    },
    updateCheckWeight: (state, payload) => {
      state.checklist.checks = state.checklist.checks.map(check => {
        if (+check.id === +payload.checkId) {
          return {
            ...check,
            percentage_weight: +payload.value
          };
        }
        return check
      })
    },
    resetCheckWeights: (state, total) => {
      const checksCount = state.checklist.checks.length
      if (checksCount === 0) return

      const customTotal = Number(total) || 100

      const baseWeight = Math.floor(customTotal / checksCount)
      const remainder = customTotal - (baseWeight * checksCount)

      // Assign baseWeight to all, and add 1 to the first `remainder` checks
      state.checklist.checks = state.checklist.checks.map((check, index) => ({
        ...check,
        percentage_weight: baseWeight + (index < remainder ? 1 : 0)
      }))
    },
    clearCheckWeights(state) {
      if (!state.checklist?.checks) return

      state.checklist.checks = state.checklist.checks.map(check => ({
        ...check,
        percentage_weight: null
      }))
    }
  },

  actions: {
    loadChecklists: ({commit}, path) => new Promise((resolve, reject) => {
      commonBackend.loadPath(path, response => {
        commit('setChecklists', response.data)
        resolve()
      }, error => reject(error))
    }),
    loadChecklistList: ({commit}) => new Promise((resolve, reject) => {
      backend.loadChecklistList(response => {
        commit('setChecklistsList', response.data)
        resolve()
      }, error => reject(error))
    }),
    loadChecklistSelectList: ({commit}) => new Promise((resolve, reject) => {
      backend.loadChecklistSelectList(response => {
        commit('setChecklistsList', response.data)
        resolve()
      }, error => reject(error))
    }),
    searchChecklistSelectList: ({ commit }, search) => new Promise((resolve, reject) => {
      backend.searchChecklistSelectList(search, ({ data }) => {
        commit('setChecklistsList', data)
        resolve(data)
      }, error => reject(error))
    }),
    loadChecklist: ({commit}, checklistId) => new Promise((resolve, reject) => {
      backend.loadChecklist(checklistId, response => {
        commit('setChecklist', response.data)
        resolve()
      }, error => reject(error))
    }),
    loadChecklistVersions: ({commit}, checklistId) => new Promise((resolve, reject) => {
      backend.loadChecklistVersions(checklistId, response => {
        commit('setChecklistVersions', response.data)
        resolve()
      }, error => reject(error))
    }),
    store: ({state}) => new Promise((resolve, reject) => {
      let checklist = {...state.checklist}
      checklist.checks = checklist.checks.map(check => ({
        id: check.id,
        percentage_weight: check.percentage_weight ?? 0
      }))
      backend.store(checklist, response => {
        resolve(response.data)
      }, error => reject(error))
    }),
    save: ({ state, commit }) => new Promise((resolve, reject) => {
      let checklist = { ...state.checklist }
      checklist.checks = checklist.checks.map(check => ({
        id: check.id,
        percentage_weight: check.percentage_weight ?? 0
      }))
      backend.save(checklist, response => {
        commit('setChecklist', response.data)
        resolve()
      }, error => reject(error))
    }),
    deleteChecklist: ({state}) => new Promise((resolve, reject) => {
      backend.deleteChecklist(state.checklist.id, response => {
        resolve(response.data)
      }, error => reject(error))
    }),
    duplicate: ({state}, detach) => new Promise((resolve, reject) => {
      backend.duplicate({checklistId: state.checklist.id, detach: detach}, response => {
        resolve(response.data)
      }, error => reject(error))
    }),
    syncAssetTypes: ({state}, payload) => new Promise((resolve, reject) => {
      backend.syncAssetTypes(payload, response => {
        resolve(response.data)
      }, error => reject(error))
    }),
  },

  getters: {
    checklists: state => state.checklists,
    checklist: state => state.checklist,
    currChecklist: state => state.currChecklist,
    checklistVersions: state => state.checklistVersions.sort((a, b) => a.version < b.version ? 1 : -1),
    comparedChecklistVersions: (state) => {
      const result = [];

      // Loop through each version, comparing each with its previous version
      for (let i = 1; i < state.checklistVersions.length; i++) {
        const previousChecklist = state.checklistVersions[i - 1];
        const currentChecklist = state.checklistVersions[i];

        // Merge the checks with the change type for this version
        const mergedChecks = mergeChecksWithChangeTypes(
            previousChecklist.checks,
            currentChecklist.checks
        );

        // Add current checklist with annotated checks to result
        result.push({ ...currentChecklist, check_changes: mergedChecks });
      }

      return result.sort((a, b) => a.version < b.version ? 1 : -1);
    },
    checklistList: state => state.checklistList,
    checklistCustomTotal: state => {
      return state.checklist?.checks?.reduce(
        (sum, check) => sum + Number(check.percentage_weight ?? 0),
        0
      ) ?? 0;
    }
  }

}

export default checklist
